/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-shadow */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Form } from 'informed';
import { Spin } from 'antd';
import moment from 'moment';
import { MONTHS } from '@common/constants';
import { getErrors } from '@common/utils';
import { IVehiclesChecklistDetails } from '@common/interfaces';
import { useDrivers, useFacility, useVehicles } from '@common/hooks';
import { Loader } from '@components';
import { useRepository } from '@context';
import { Dropdown } from '@ui-modules/informed';
import { IDropdownOption } from '@ui-modules/types';
import { Button } from '@unbooking/ui-modules';
import { VehiclesChecklistCalendar, DriverInfoPopup } from './components';
import { Serializer } from './utils';
import './styles.scss';

const YEARS: IDropdownOption[] = [
  { label: `${moment().year() - 1}`, value: `${moment().year() - 1}` },
  { label: `${moment().year()}`, value: `${moment().year()}` },
];

const VehiclesChecklistPage: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    agencyName,
    facility: { city, country, uuid: facilityId, agencyId },
    isLoading: isFacilityLoading,
  } = useFacility();

  const { vehiclesChecklistRepository } = useRepository();
  const { driverOptions } = useDrivers();
  const { vehicleOptions, vehicles } = useVehicles();

  const weekStartDate = moment().day(1).toISOString().split('T')[0];
  const weekEndDate = moment().day(7).toISOString().split('T')[0];

  const [currentDate, setCurrentDate] = useState<Date>(moment().startOf('week').toDate());
  const [month, setOverviewMonth] = useState<number>();
  const [year, setOverviewYear] = useState<number>(moment().year());
  const [createdAfter, setCreatedAfter] = useState<string>(weekStartDate);
  const [createdBefore, setCreatedBefore] = useState<string>(weekEndDate);

  const [selectedDriver, setSelectedDriver] = useState<IDropdownOption>();
  const [selectedVehicle, setSelectedVehicle] = useState<IDropdownOption>();

  const [selectedChecklistId, setSelectedChecklistId] = useState<string | null>(null);
  const [checklistDetails, setChecklistDetails] = useState<IVehiclesChecklistDetails | null>();

  const {
    data,
    refetch,
    isLoading: checklistsLoading,
  } = useQuery(
    'get-checklists',
    async () =>
      vehiclesChecklistRepository.getChecklists({
        agencyId,
        locationId: facilityId,
        createdAfter,
        createdBefore,
        driverId: selectedDriver?.value,
      }),
    { refetchOnWindowFocus: false, enabled: false },
  );

  useEffect(() => {
    if (agencyId && facilityId) {
      refetch();
    }
  }, [selectedDriver, refetch, createdAfter, createdBefore, agencyId, facilityId]);

  useQuery(
    ['get-checklist-details', selectedChecklistId],
    () => vehiclesChecklistRepository.getChecklistDetails(selectedChecklistId!),
    {
      refetchOnWindowFocus: false,
      enabled: !!selectedChecklistId,
      onSuccess(data: IVehiclesChecklistDetails) {
        setChecklistDetails(data);
      },
      onError(err: any) {
        toast.error(getErrors(err.response));
      },
    },
  );

  useEffect(() => {
    if (month) {
      const date = moment({ month, year });
      const newCreatedBefore = date.endOf('month').toISOString().split('T')[0];
      const newCreatedAfter = date.startOf('month').toISOString().split('T')[0];

      setCreatedBefore(newCreatedBefore);
      setCreatedAfter(newCreatedAfter);
    }
  }, [month, year]);

  useEffect(() => {
    if (currentDate) {
      const date = moment(currentDate);
      const newCreatedAfter = date.day(1).toISOString().split('T')[0];
      const newCreatedBefore = date.day(7).toISOString().split('T')[0];

      setCreatedBefore(newCreatedBefore);
      setCreatedAfter(newCreatedAfter);
    }
  }, [currentDate]);

  const visibleVehicles = useMemo(
    () =>
      vehicles.filter(({ id }) => {
        if (selectedVehicle) {
          return selectedVehicle?.value === id;
        }
        return true;
      }),
    [vehicles, selectedVehicle],
  );

  const schedulerVehicles = useMemo(
    () => Serializer.mapVehiclesForScheduler(visibleVehicles, data?.results ?? []),
    [visibleVehicles, data],
  );

  const schedulerChecklists = useMemo(
    () => Serializer.mapChecklistsForScheduler(data?.results ?? []),
    [data],
  );

  return (
    <section className="vehicles-checklist">
      <Spin size="large" spinning={isFacilityLoading}>
        <header className="vehicles-checklist-header">
          <section className="vehicles-checklist-filters">
            <h1>
              {`${t('mobility.vehiclesChecklist')} - `}
              <span>
                {agencyName} {country}, {city}
              </span>
            </h1>
            <Form className="filters">
              <div className="driver">
                <Dropdown
                  className="dropdown outline"
                  name="filterByDriver"
                  options={driverOptions}
                  value={selectedDriver}
                  placeholder={t('mobility.filterByDriver')}
                  onChange={({ value }) => {
                    const selectedDriver = driverOptions.find(
                      ({ value: driverId }) => driverId === value,
                    );
                    setSelectedDriver(selectedDriver);
                  }}
                />
              </div>
              <div className="vehicle">
                <Dropdown
                  className="dropdown outline"
                  name="filterByVehicle"
                  options={vehicleOptions}
                  value={selectedVehicle}
                  placeholder={t('mobility.filterByVehicle')}
                  onChange={({ value }) => {
                    const selectedVehicle = vehicleOptions.find(
                      ({ value: vehicleId }) => value === vehicleId,
                    );
                    setSelectedVehicle(selectedVehicle);
                  }}
                />
              </div>
              <Button
                className="btn btn-reset"
                text={t('common.btnResetFilters')}
                variant="link"
                onClick={() => {
                  setSelectedVehicle(undefined);
                  setSelectedDriver(undefined);
                }}
              />
            </Form>
          </section>
          <section className="vehicles-checklist-info">
            <section className="vehicles-checklist-info-overview">
              <div>
                <Form className="filters">
                  <div className="overview">
                    <h3>{t('mobility.overviewFor')}</h3>
                    <Dropdown
                      className="dropdown outline"
                      name="overviewMonth"
                      options={MONTHS}
                      defaultValue={MONTHS.find(
                        ({ value }) => value === String(moment().month() + 1),
                      )}
                      onChange={(option: IDropdownOption) => setOverviewMonth(+option.value - 1)}
                    />
                    <Dropdown
                      className="dropdown outline"
                      name="overviewYear"
                      options={YEARS}
                      defaultValue={YEARS.find(({ value }) => value === String(moment().year()))}
                      onChange={(option: IDropdownOption) => setOverviewYear(+option.value)}
                    />
                  </div>
                </Form>
                <ul className="checklist">
                  <li className="clear">
                    {data?.aggregated.clear_number ?? 0} {t('mobility.clear')}
                  </li>
                  <li className="critical">
                    {data?.aggregated.critical_issues_number ?? 0} {t('mobility.critical')}
                  </li>
                  <li className="not-reported">
                    {data?.aggregated.not_submitted_number ?? 0} {t('mobility.notReported')}
                  </li>
                  <li className="issues">
                    {data?.aggregated.issues_number ?? 0} {t('mobility.issues')}
                  </li>
                  <li className="not-used">
                    {data?.aggregated.not_used ?? 0} {t('mobility.notUsed')}
                  </li>
                </ul>
              </div>
              <div className="daily-checks">
                <h2>0</h2>
                <small>{t('mobility.dailyChecks')}</small>
              </div>
            </section>
          </section>
        </header>
        <main>
          {checklistsLoading ? (
            <Loader fullScreen />
          ) : (
            visibleVehicles.length && (
              <VehiclesChecklistCalendar
                checklists={schedulerChecklists}
                schedulerDate={currentDate}
                vehicles={schedulerVehicles}
                getChecklistDetails={(id) => {
                  setSelectedChecklistId(id);
                }}
                goToChecklistDetailsPage={(id) => {
                  navigate(`/mobility-management/vehicles-checklist/${id}`);
                }}
                onDateChange={(newDate: Date) => setCurrentDate(newDate)}
              />
            )
          )}
          {checklistDetails && (
            <DriverInfoPopup
              checklist={checklistDetails}
              onClosePopup={() => {
                setChecklistDetails(null);
                setSelectedChecklistId(null);
              }}
            />
          )}
        </main>
      </Spin>
    </section>
  );
};

export default VehiclesChecklistPage;
